import axios from "axios";
import keycloak from "../keycloak";
import { config } from "./environment"

const axiosInstance = axios.create({
    baseURL: config.API_BASE_URL, // Base URL for the API
});

// Request interceptor: Attach the token to every request.
// We read directly from keycloak.token (in-memory) instead of a cookie
// to avoid storing sensitive tokens in browser storage.
axiosInstance.interceptors.request.use(
    (config) => {
        const token = keycloak.token;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor: Handle 401 errors and refresh the token
axiosInstance.interceptors.response.use(
    (response) => response, // Pass through successful responses
    async (error) => {
        const originalRequest = error.config;

        // Check if it's a 401 error and the token refresh hasn't been attempted
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true; // Mark this request as retried to avoid loops

            try {
                // Ensure the token is still valid or refresh it
                const refreshed = await keycloak.updateToken(30); // 30 seconds is a buffer for refreshing

                if (refreshed) {
                    // Token refreshed in keycloak.token (in-memory) — re-read it directly
                    const newToken = keycloak.token;
                    originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

                    // Retry the original request with the new token
                    return axiosInstance(originalRequest);
                }
            } catch (refreshError) {
                console.error("Token refresh failed. Redirecting to login.");
                await keycloak.logout(); // Redirect to login if token refresh fails
                return Promise.reject(refreshError);
            }
        }

        // For non-401 errors, reject the promise
        return Promise.reject(error);
    }
);

export default axiosInstance;
